/* eslint-disable react/no-multi-comp -- shared linked-offer row building blocks (static + reorder) */
import React from 'react';

import {
  Box,
  Button,
  Chip,
  MenuItem,
  Select,
  Stack,
  TableCell,
  TableRow,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';

import type { MatchedOffer } from '~/types/attractionTypes';

const MATCH_TYPE_LABELS: Record<string, string> = {
  DIRECT_TITLE: 'Direct Title',
  AI_SEMANTIC: 'AI Semantic',
  PROXIMITY: 'Proximity',
  KEYWORD: 'Keyword',
  MANUAL: 'Manual',
};

const MATCH_TYPE_COLORS: Record<string, 'default' | 'success' | 'info' | 'warning' | 'primary' | 'secondary'> = {
  DIRECT_TITLE: 'success',
  AI_SEMANTIC: 'primary',
  PROXIMITY: 'info',
  KEYWORD: 'warning',
  MANUAL: 'secondary',
};

type MutationLike<TVariables> = {
  isPending: boolean;
  variables?: TVariables;
  mutate: (vars: TVariables, opts?: { onSuccess?: () => void }) => void;
};

type RowMutations = {
  excludeMutation: MutationLike<{ attractionId: string; offerId: string }>;
  restoreMutation: MutationLike<{ attractionId: string; offerId: string }>;
};

/** Minimal history surface for row navigation */
export type OfferListHistory = { push: (path: string) => void };

type OfferCellsProps = RowMutations & {
  offer: MatchedOffer;
  attractionId: string;
  history: OfferListHistory;
  /** When false, row-level navigation is used instead */
  navigateOnTitleOnly?: boolean;
  disableActions?: boolean;
};

function LinkedOfferCells({
  offer,
  attractionId,
  history,
  excludeMutation,
  restoreMutation,
  navigateOnTitleOnly,
  disableActions,
}: OfferCellsProps) {
  const theme = useTheme();
  const relevancePct = Math.round((offer.relevanceScore ?? 0) * 100);
  const relevanceColor = relevancePct >= 80 ? 'success.main' : relevancePct >= 50 ? 'warning.main' : 'error.main';

  const goToOffer = () => history.push(`/experience/${offer.id}`);

  return (
    <>
      <TableCell sx={{ py: 1.25, px: 1.5 }} onClick={navigateOnTitleOnly ? goToOffer : undefined}>
        <Typography
          sx={{
            fontWeight: 500,
            fontSize: '0.82rem',
            lineHeight: 1.4,
            display: '-webkit-box',
            WebkitLineClamp: 1,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            ...(navigateOnTitleOnly && {
              cursor: 'pointer',
              textDecoration: 'underline',
              textDecorationColor: alpha(theme.palette.primary.main, 0.35),
            }),
          }}
        >
          {offer.title}
        </Typography>
      </TableCell>
      <TableCell sx={{ py: 1.25, px: 1, textAlign: 'center' }}>
        <Chip
          label={offer.provider}
          size="small"
          variant="outlined"
          sx={{ fontSize: '0.7rem', height: 22, fontWeight: 600 }}
        />
      </TableCell>
      <TableCell sx={{ py: 1.25, px: 1, textAlign: 'center' }}>
        <Chip
          label={MATCH_TYPE_LABELS[offer.matchType] ?? offer.matchType}
          size="small"
          color={MATCH_TYPE_COLORS[offer.matchType] ?? 'default'}
          sx={{ fontSize: '0.65rem', height: 22 }}
        />
      </TableCell>
      <TableCell sx={{ py: 1.25, px: 1.5, minWidth: 100 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box
            sx={{
              flex: 1,
              height: 4,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.grey[300], 0.5),
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                height: '100%',
                width: `${relevancePct}%`,
                borderRadius: 2,
                bgcolor: relevanceColor,
                transition: 'width 0.3s',
              }}
            />
          </Box>
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.7rem',
              fontWeight: 600,
              minWidth: 28,
              textAlign: 'right',
              color: relevanceColor,
            }}
          >
            {relevancePct}%
          </Typography>
        </Stack>
      </TableCell>
      <TableCell sx={{ py: 1.25, px: 1, textAlign: 'center' }}>
        {offer.excluded ? (
          <Button
            size="small"
            variant="outlined"
            color="success"
            disabled={disableActions || (restoreMutation.isPending && restoreMutation.variables?.offerId === offer.id)}
            onClick={(e) => {
              e.stopPropagation();
              restoreMutation.mutate({ attractionId, offerId: offer.id });
            }}
            sx={{ fontSize: '0.7rem', py: 0.25 }}
          >
            Restore
          </Button>
        ) : (
          <Button
            size="small"
            variant="outlined"
            color="error"
            disabled={disableActions || (excludeMutation.isPending && excludeMutation.variables?.offerId === offer.id)}
            onClick={(e) => {
              e.stopPropagation();
              excludeMutation.mutate({ attractionId, offerId: offer.id });
            }}
            sx={{ fontSize: '0.7rem', py: 0.25 }}
          >
            Exclude
          </Button>
        )}
      </TableCell>
    </>
  );
}

export type StaticLinkedOfferRowProps = OfferCellsProps;

export function StaticLinkedOfferRow(props: StaticLinkedOfferRowProps) {
  const theme = useTheme();
  const { history, offer } = props;
  return (
    <TableRow
      sx={{
        cursor: 'pointer',
        '&:last-child td': { borderBottom: 0 },
        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
        transition: 'background-color 0.15s ease',
      }}
      onClick={() => history.push(`/experience/${offer.id}`)}
    >
      <LinkedOfferCells {...props} navigateOnTitleOnly={false} />
    </TableRow>
  );
}

type ReorderLinkedOfferRowProps = StaticLinkedOfferRowProps & {
  rowIndex: number;
  rankLimit: number;
  onRankChange: (offerId: string, rawValue: string) => void;
};

function ReorderLinkedOfferRow({ rowIndex, rankLimit, onRankChange, offer, ...cellProps }: ReorderLinkedOfferRowProps) {
  const theme = useTheme();
  const currentRank = rowIndex + 1;

  return (
    <TableRow
      sx={{
        '&:last-child td': { borderBottom: 0 },
        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
        transition: 'background-color 0.15s ease',
      }}
    >
      <TableCell
        sx={{
          py: 0.75,
          px: 0.75,
          width: 72,
          verticalAlign: 'middle',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Select<number | ''>
          size="small"
          value={currentRank}
          displayEmpty
          onChange={(e) => onRankChange(offer.id, String(e.target.value))}
          sx={{
            minWidth: 56,
            '& .MuiSelect-select': { py: 0.5, fontSize: '0.8125rem' },
          }}
        >
          <MenuItem value="">
            <Typography variant="body2" color="text.disabled">
              -
            </Typography>
          </MenuItem>
          {Array.from({ length: rankLimit }, (_, i) => i + 1).map((n) => {
            const showSwapHint = n !== currentRank;
            return (
              <MenuItem key={n} value={n}>
                {n}
                {showSwapHint && (
                  <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 0.5 }}>
                    (swap)
                  </Typography>
                )}
              </MenuItem>
            );
          })}
        </Select>
      </TableCell>
      <LinkedOfferCells {...cellProps} offer={offer} navigateOnTitleOnly disableActions />
    </TableRow>
  );
}

type ReorderBodyProps = RowMutations & {
  offers: Array<MatchedOffer>;
  attractionId: string;
  history: OfferListHistory;
  onReorder: (next: Array<MatchedOffer>) => void;
};

export function ReorderableOffersTableBody({
  offers,
  attractionId,
  history,
  excludeMutation,
  restoreMutation,
  onReorder,
}: ReorderBodyProps) {
  const rankLimit = offers.length;

  const handleRankChange = (offerId: string, raw: string) => {
    if (raw === '') {
      return;
    }
    const targetRank = Number(raw);
    if (!Number.isFinite(targetRank) || targetRank < 1 || targetRank > rankLimit) {
      return;
    }
    const fromIndex = offers.findIndex((o) => o.id === offerId);
    const toIndex = targetRank - 1;
    if (fromIndex < 0 || fromIndex === toIndex) {
      return;
    }
    const next = [...offers];
    [next[fromIndex], next[toIndex]] = [next[toIndex], next[fromIndex]];
    onReorder(next);
  };

  return (
    <>
      {offers.map((offer, rowIndex) => (
        <ReorderLinkedOfferRow
          key={offer.id}
          rowIndex={rowIndex}
          rankLimit={rankLimit}
          offer={offer}
          attractionId={attractionId}
          history={history}
          excludeMutation={excludeMutation}
          restoreMutation={restoreMutation}
          onRankChange={handleRankChange}
        />
      ))}
    </>
  );
}
